import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Container from '@/components/Container/Container';
import {
  Button,
  PageHeader,
  SpinningLoader,
  Typography,
  VerticalTabs,
} from 'br2s-ui-componentlibrary_r2_02';
import { PROFILE_TYPES } from './profileSelection-constant';
import './ProfileSelection.scss';
import { SystemIcon } from 'br2s-ui-componentlibrary_r2_02/dist/foundation/assets/system-icon';
import ProfileSelectionForm from './ProfileSelectionForm';
import {
  generateAvailableTabKeys,
  hasNoProfilesToSwitch,
  transformToProfileEntitiesObject,
} from './profileSelection-utilities';
import { LocalStorageService } from 'br2s-ui-common-utils-02';
import NoProfileSwitchModal from '@/components/NoProfileSwitchModal/NoProfileSwitchModal';
import useNavigateGuard from '@/utilities/hooks/useNavigateGuard';
import useProfileSwitch from '@/utilities/hooks/useProfileSwitch/useProfileSwitch';

const ProfileSelection = () => {
  const navigate = useNavigate();
  const guardedNavigate = useNavigateGuard();

  const { fetched, loading, entities, uiState, fetchProfiles, setActiveTab } =
    useProfileSwitch();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { user } = LocalStorageService.getAuthInfo('user');

  const entitiesObject = transformToProfileEntitiesObject(entities);

  useEffect(() => {
    if (!fetched) {
      fetchProfiles();
    } else if (hasNoProfilesToSwitch(entitiesObject)) {
      setIsModalOpen(true);
    }
  }, [fetched]);

  // istanbul ignore next
  const handleModalClick = () => {
    setIsModalOpen(false);
    navigate('/dashboard');
    document.body.style.overflow = 'unset';
  };

  const tabs = generateAvailableTabKeys(entitiesObject).map((key, index) => ({
    id: index,
    label: PROFILE_TYPES[key].label,
    onClick: () => setActiveTab(index),
  }));

  return (
    <div className="switch-profile-page">
      <PageHeader
        id="switch-profile-page-header"
        title="My profile"
        description={
          user?.name
            ? `You are logged in as ${user?.name} (NRIC ${user?.uin}).`
            : `You are logged in as ${user?.uin}.`
        }
        breadcrumbData={[
          {
            label: 'Home',
            onClick: () => guardedNavigate('/'),
          },
          {
            label: 'My profile',
          },
        ]}
      />
      <Container>
        <div className="switch-profile-page--content">
          <div className="switch-profile-page--content-header">
            <Button
              id="switch-profile-back"
              type="quiet"
              iconType="prepended"
              iconClassName={SystemIcon.NavigationArrowBack}
              label="Back"
              onClick={() => {
                guardedNavigate('/dashboard');
              }}
            />
            <Typography
              type="headline-3"
              label="Select profile"
              weight="bold"
            />
            <Typography
              type="base"
              label="Select a profile and entity to proceed."
            />
          </div>
          {loading ? (
            <SpinningLoader />
          ) : (
            <div className="switch-profile-page--content-body">
              {uiState.tabIndex >= 0 && (
                <VerticalTabs
                  tabs={tabs}
                  activeIndex={uiState.tabIndex}
                  id="profile-selection-vertical-tabs"
                  isHaveBorder
                  scrollToTop
                >
                  <ProfileSelectionForm />
                </VerticalTabs>
              )}
            </div>
          )}
        </div>
      </Container>

      <NoProfileSwitchModal
        isOpen={isModalOpen}
        onClick={handleModalClick}
        onCloseClick={handleModalClick}
      />
    </div>
  );
};

export default ProfileSelection;
